/**
 * Utility helpers for customer components.
 *
 * Status colours map to the shared, dark-mode-safe tones in `_shared.tsx` so a
 * badge reads correctly on both light and dark surfaces.
 */
import type { Customer } from '@repo/types';
import { TONE_CLASSES, type Tone } from './_shared';

/**
 * Current role/company for display, falling back to the current (no end date)
 * or most recent employment entry when the scalar current-role fields aren't
 * set — mirrors the fallback on the web app's profile hero, so admin and
 * customer see the same thing.
 */
export const getCurrentRole = (customer: Customer): { title?: string; company?: string } => {
  const history = customer.employmentHistory ?? [];
  const recent = history.find((e) => !e.end_date) ?? history[0];
  return {
    title: customer.currentJobTitle || recent?.designation || undefined,
    company: customer.currentCompany || recent?.company_name || undefined,
  };
};

export const getJobSearchStatusTone = (status?: string): Tone => {
  const tones: Record<string, Tone> = {
    actively_looking: 'success',
    passively_looking: 'warning',
    not_looking: 'neutral',
    hired: 'info',
  };
  return (status && tones[status]) || 'neutral';
};

export const getAvailabilityStatusTone = (status?: string): Tone => {
  const tones: Record<string, Tone> = {
    immediately: 'success',
    within_2_weeks: 'info',
    within_month: 'warning',
    within_3_months: 'warning',
    flexible: 'violet',
  };
  return (status && tones[status]) || 'neutral';
};

export const getJobSearchStatusColor = (status?: string): string =>
  TONE_CLASSES[getJobSearchStatusTone(status)];

export const getAvailabilityStatusColor = (status?: string): string =>
  TONE_CLASSES[getAvailabilityStatusTone(status)];

export const formatCareerLevel = (level?: string): string | null => {
  if (!level) return null;
  return level.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

export const formatWorkMode = (mode?: string): string | null => {
  if (!mode) return null;
  return mode.charAt(0).toUpperCase() + mode.slice(1);
};

/** Title-case a snake_case / lower enum value for display. */
export const humanize = (value?: string | null): string => {
  if (!value) return '';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};
